import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  eventHubObservabilityTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { EventHubApiService, EventHubAlertItem, EventHubMetricsSnapshot } from './event-hub-api.service';

@Component({
  standalone: true,
  selector: 'ain-event-hub-observability',
  imports: [DecimalPipe, AinPageComponent, AinStateComponent, RouterLink, ButtonModule, TableModule],
  template: `
    <ain-page [title]="t('eventHub.observability.title')" [description]="t('eventHub.observability.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.loadFailed')" [message]="error() ?? ''" />
      } @else {
        <div class="toolbar">
          <button pButton type="button" severity="secondary" [label]="t('eventHub.observability.refresh')" (click)="load()"></button>
          <span class="live-status" [class]="liveUpdateService.connectionState()">
            {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
          </span>
        </div>

        <section class="metrics">
          <article>
            <strong>{{ t('eventHub.observability.metrics.outboxLag') }}</strong>
            <span>{{ metrics()?.outboxLagSeconds ?? 0 | number:'1.0-0' }}s</span>
          </article>
          <article>
            <strong>{{ t('eventHub.observability.metrics.dlqDepth') }}</strong>
            <span>{{ metrics()?.dlqDepth ?? 0 | number:'1.0-0' }}</span>
          </article>
          <article>
            <strong>{{ t('eventHub.observability.metrics.dispatchThroughput') }}</strong>
            <span>{{ metrics()?.dispatchThroughputPerMinute ?? 0 | number:'1.0-0' }}/min</span>
          </article>
          <article>
            <strong>{{ t('eventHub.observability.metrics.webhookFailureRate') }}</strong>
            <span>{{ (metrics()?.webhookFailureRate ?? 0) * 100 | number:'1.0-1' }}%</span>
          </article>
          <article>
            <strong>{{ t('eventHub.observability.metrics.handlerLatencyP95') }}</strong>
            <span>{{ metrics()?.handlerLatencyP95Ms ?? 0 | number:'1.0-1' }}ms</span>
          </article>
          <article>
            <strong>{{ t('eventHub.observability.metrics.collectedAt') }}</strong>
            <span>{{ metrics()?.collectedAtUtc ?? '—' }}</span>
          </article>
        </section>

        <section class="links">
          <a [routerLink]="['/event-hub/catalog']">{{ t('nav.eventHub.catalog') }}</a>
          <a [routerLink]="['/event-hub/webhook-subscriptions']">{{ t('nav.eventHub.webhooks') }}</a>
        </section>

        <section class="alerts">
          <h3>{{ t('eventHub.observability.alerts.title') }}</h3>
          @if (!alerts().length) {
            <ain-state type="empty" [title]="t('eventHub.observability.alerts.empty')" />
          } @else {
            <p-table [value]="alerts()" dataKey="code">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('eventHub.observability.alerts.severity') }}</th>
                  <th>{{ t('eventHub.observability.alerts.code') }}</th>
                  <th>{{ t('eventHub.observability.alerts.message') }}</th>
                  <th>{{ t('eventHub.observability.alerts.raisedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr [class.alert-high]="row.severity === 'high'" [class.alert-critical]="row.severity === 'critical'">
                  <td>{{ t(severityKey(row.severity)) }}</td>
                  <td>{{ t(row.code) }}</td>
                  <td>{{ row.message }}</td>
                  <td>{{ row.raisedAtUtc }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
    .live-status.connected { color: #15803d; }
    .live-status.connecting, .live-status.reconnecting { color: #a16207; }
    .live-status.disconnected { color: #b91c1c; }
    .metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px; }
    .metrics article { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; display: grid; gap: 6px; }
    .metrics strong { color: #6b7280; font-size: 0.85rem; }
    .metrics span { font-size: 1.25rem; font-weight: 700; }
    .links { display: flex; gap: 12px; margin-bottom: 16px; }
    .links a { color: #1d4ed8; text-decoration: none; font-weight: 600; }
    .alerts h3 { margin: 0 0 8px; }
    .alert-high td { background: #fff7ed; }
    .alert-critical td { background: #fef2f2; }
    @media (max-width: 760px) { .metrics { grid-template-columns: 1fr; } }
  `]
})
export class EventHubObservabilityComponent implements OnInit, OnDestroy {
  private readonly api = inject(EventHubApiService);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;
  private liveSubscription: Subscription | null = null;

  readonly loading = signal(true);
  readonly error = signal('');
  readonly metrics = signal<EventHubMetricsSnapshot | null>(null);
  readonly alerts = signal<EventHubAlertItem[]>([]);

  liveStatusTranslationKey = liveStatusTranslationKey;

  ngOnInit(): void {
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.liveSubscription?.unsubscribe();
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.getObservabilityMetrics().subscribe({
      next: metrics => {
        this.metrics.set(metrics);
        this.error.set('');
        this.api.getObservabilityAlerts().subscribe({
          next: alerts => {
            this.alerts.set(alerts);
            this.loading.set(false);
          },
          error: () => {
            this.alerts.set([]);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.metrics.set(null);
        this.alerts.set([]);
        this.error.set(this.t('eventHub.observability.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  severityKey(severity: string): string {
    return `eventHub.observability.severity.${severity}`;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = eventHubObservabilityTopic(tenantId);
    await this.liveUpdateService.subscribe(topic);
    this.liveSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.eventHubObservabilityChanged))
      .subscribe(() => this.load());
    this.liveUnsubscribe = async () => {
      this.liveSubscription?.unsubscribe();
      await this.liveUpdateService.unsubscribe(topic);
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
